import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, ShieldCheck, Zap, Globe, Layers, Cpu, ArrowUpRight, Scale
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
const C = {
  bgDark: '#0B5867',   // Deep Teal
  bgMid: '#2B6873',   // Mid Teal
  bgLight: '#9FBCC0',   // Mist
  accent: '#FF8C7A',   // Coral
  success: '#4ade80',   // Green
  white: '#FFFFFF',
  textSecondary: '#E2E8F0',
  glass: 'rgba(255, 255, 255, 0.08)',
  border: 'rgba(255, 255, 255, 0.15)',
};

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@300;400;600&display=swap');

  body { 
    margin: 0;
    font-family: 'Inter', sans-serif; 
    background-color: ${C.bgDark}; 
    color: ${C.white}; 
    overflow-x: hidden;
  }

  .main-bg {
    background: 
      radial-gradient(circle at 50% 50%, ${C.bgLight} 0%, transparent 70%),
      linear-gradient(180deg, ${C.bgDark} 0%, ${C.bgMid} 45%, ${C.bgMid} 55%, ${C.bgDark} 100%);
    background-attachment: fixed;
    min-height: 100vh;
  }

  .reveal-node {
    transition: transform 0.8s cubic-bezier(0.17, 0.67, 0.83, 0.67), opacity 0.8s ease;
    transform: perspective(1000px) rotateX(10deg) translateY(30px);
    opacity: 0;
  }
  .reveal-node.visible {
    transform: perspective(1000px) rotateX(0deg) translateY(0);
    opacity: 1;
  }

  .barbell-bar {
    position: relative;
    height: 8px;
    background: linear-gradient(90deg, ${C.accent} 0%, rgba(255,255,255,0.2) 50%, ${C.success} 100%);
    border-radius: 4px;
    margin: 40px 0;
  }

  .barbell-weight-left {
    position: absolute;
    left: -10px;
    top: -16px;
    width: 24px;
    height: 40px;
    background: ${C.accent};
    border-radius: 6px;
    box-shadow: 0 0 15px ${C.accent}aa;
  }

  .barbell-weight-right {
    position: absolute;
    right: -10px;
    top: -16px;
    width: 24px;
    height: 40px;
    background: ${C.success};
    border-radius: 6px;
    box-shadow: 0 0 15px ${C.success}aa;
  }
`;

// ── Shared Component: Vertical Card ──
function VerticalCard({ title, desc, icon: Icon, index }) {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, { threshold: 0.1 });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`reveal-node ${isVisible ? 'visible' : ''}`}
      style={{
        background: 'rgba(255, 255, 255, 0.03)',
        border: `1px solid ${C.border}`,
        borderRadius: '32px',
        padding: '48px',
        transitionDelay: isVisible ? `${index * 100}ms` : '0ms'
      }}
    >
      <div style={{ color: C.accent, marginBottom: '24px' }}>
        <Icon size={40} />
      </div>
      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', marginBottom: '16px', color: C.white }}>{title}</h3>
      <p style={{ color: C.white, opacity: 0.7, lineHeight: 1.7, fontSize: '0.95rem' }}>{desc}</p>
    </div>
  );
}

// ── AboutUs Layout ──
const AboutUs = () => {
  const barbellRef = useRef(null);
  const [barbellVisible, setBarbellVisible] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = GLOBAL_STYLES;
    document.head.appendChild(style);

    const observer = new IntersectionObserver(([entry]) => {
      setBarbellVisible(entry.isIntersecting);
    }, { threshold: 0.2 });

    if (barbellRef.current) observer.observe(barbellRef.current);

    return () => {
      style.remove();
      observer.disconnect();
    };
  }, []);

  const verticals = [
    {
      title: 'Direct Equity & Proprietary Trading',
      desc: 'We engage in active cash equity markets, utilizing technical and fundamental analysis to capture short-to-medium term market inefficiencies.',
      icon: TrendingUp
    },
    {
      title: 'Institutional PMS Investments',
      desc: 'We allocate significant capital to SEBI-registered Portfolio Management Services, gaining access to specialized investment themes and professional fund management.',
      icon: Layers
    },
    {
      title: 'Treasury Management',
      desc: 'Our liquidity is optimized through high-yield Fixed Deposits and Debt instruments, ensuring capital preservation and immediate readiness for market opportunities.',
      icon: Zap
    },
    {
      title: 'Real-Time Analytics',
      desc: 'We believe in transparency. We are currently developing an internal dashboard to track our portfolio with daily performance reports and MoM growth metrics.',
      icon: Cpu
    },
  ];

  return (
    <div className="main-bg">
      <Header />

      {/* Hero Section / Who We Are */}
      <section style={{ padding: '220px 30px 100px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'inline-block', padding: '8px 20px', background: 'rgba(255,255,255,0.04)', borderRadius: '50px', marginBottom: '32px', border: '1px solid rgba(255,255,255,0.1)' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, letterSpacing: '4px', color: C.accent }}>WHO WE ARE</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '60px', alignItems: 'start' }}>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(3rem, 6vw, 4.5rem)', lineHeight: 1.1, marginBottom: '30px', color: C.white }}>
              Disciplined <br /> Capital Allocators.
            </h1>
            <p style={{ fontSize: '1.15rem', color: C.white, opacity: 0.9, marginBottom: '30px', lineHeight: 1.8 }}>
              KKB consulting service is a tech-centric proprietary investment firm based in India, structured as a Private Limited entity to manage and scale corporate capital.
            </p>
          </div>
          <div style={{ background: 'rgba(255, 255, 255, 0.02)', border: `1px solid ${C.border}`, borderRadius: '40px', padding: '50px' }}>
            <h3 style={{ fontSize: '1.4rem', color: C.accent, fontWeight: 600, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              Strategic Business Decisions <ArrowUpRight />
            </h3>
            <p style={{ fontSize: '1.1rem', color: C.white, opacity: 0.8, lineHeight: 1.7, marginBottom: '20px' }}>
              We are not just market participants, we are disciplined capital allocators who treat every trade and investment as a strategic business decision.
            </p>
            <p style={{ fontSize: '1rem', color: C.white, opacity: 0.6, lineHeight: 1.6 }}>
              By integrating a sophisticated multi-asset approach, we deploy our capital across four distinct verticals.
            </p>
          </div>
        </div>
      </section>

      {/* Four Verticals */}
      <section style={{ padding: '100px 30px', maxWidth: 1300, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '3.5rem', color: C.white, marginBottom: '20px' }}>Core Verticals</h2>
          <p style={{ color: C.white, opacity: 0.7, fontSize: '1.1rem' }}>Deploying capital securely across multiple risk profiles</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px' }}>
          {verticals.map((v, i) => <VerticalCard key={`vertical-${i}`} {...v} index={i} />)}
        </div>
      </section>

      {/* Barbell Strategy & Vision */}
      <section ref={barbellRef} style={{ padding: '100px 30px', maxWidth: 1200, margin: '0 auto' }}>
        <div className={`reveal-node ${barbellVisible ? 'visible' : ''}`} style={{ background: 'rgba(255,255,255,0.02)', padding: '60px', borderRadius: '48px', border: `1px solid ${C.border}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'center' }}>
            <div>
              <span style={{ color: C.accent, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '0.85rem' }}>OUR STRATEGY</span>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.8rem', color: C.white, margin: '20px 0' }}>The "Barbell Strategy"</h2>
              <p style={{ color: C.white, opacity: 0.8, lineHeight: 1.7, marginBottom: '20px' }}>
                We are balanced on debt & aggressive, keeping safe FDs on one side, and aggressive trading on the other. This ensures absolute risk insulation while preserving the capability for high-alpha returns.
              </p>
            </div>

            {/* Interactive Barbell Graphic */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '0.9rem', fontWeight: 600 }}>
                <span style={{ color: C.accent }}>Safe FD's & Debt</span>
                <span style={{ color: C.success }}>Aggressive Trading</span>
              </div>
              <div className="barbell-bar">
                <div className="barbell-weight-left"></div>
                <div style={{ position: 'absolute', left: '50%', top: '-25px', transform: 'translateX(-50%)', color: C.white, opacity: 0.6 }}><Scale size={18} /></div>
                <div className="barbell-weight-right"></div>
              </div>
              <p style={{ fontSize: '0.85rem', color: C.white, opacity: 0.5, marginTop: '20px' }}>Perfect equilibrium of absolute safety and aggressive compounding.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section style={{ padding: '100px 30px', maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <span style={{ color: C.accent, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '0.85rem' }}>OUR VISION</span>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '3rem', color: C.white, margin: '20px 0 30px' }}>
            Building the Bridge to the <br />Next Institutional Powerhouse.
          </h2>
          <p style={{ fontSize: '1.2rem', color: C.white, opacity: 0.8, lineHeight: 1.8, marginBottom: '40px' }}>
            To transform from a proprietary trading desk into a high-alpha institutional powerhouse, leveraging a tech-first approach to master.
          </p>
          <div style={{ display: 'inline-flex', padding: '24px 40px', background: 'rgba(255,255,255,0.03)', borderRadius: '32px', border: `1px solid ${C.border}`, textAlign: 'left', maxWidth: '600px' }}>
            <p style={{ margin: 0, fontSize: '0.95rem', color: C.white, opacity: 0.7, lineHeight: 1.6 }}>
              <strong>Our Goal:</strong> To achieve compounded capital growth by building the bridge between institutional-grade financial discipline and agile, tech-driven market participation.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section style={{ padding: '40px 30px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', borderTop: `1px solid rgba(255, 255, 255, 0.1)`, paddingTop: '40px' }}>
          <p style={{ fontSize: '0.85rem', color: C.white, opacity: 0.4, fontStyle: 'italic', lineHeight: 1.6 }}>
            Disclaimer: KKB is a Proprietary Investment Company and NOT related to any SEBI Registration.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;